import { LoggingService } from "../loggingSvc";
/**
 * A contract for object that must be injected into view extensions.
 */
export interface ViewExtensionContract {
  /**
   * Context passed from the host application.
   */
  extensionContext?: any;
  /**
   * Deferral object that must be resolved to finish loading view extension.
   */
  extensionDeferral: ng.IDeferred<boolean>;
  /**
   * Function passed from the host application.
   */
  extensionHostHandler?: (options: any) => void;
}

/**
 * Exposes methods for fetching views from the view
 * extension registry.
 */
export interface ExtensibilityService {
  /**
   * Adds a view to the view extension registry.
   *
   * @param viewConfiguration The view configuration to add to the registry.
   * @returns A ViewExtensionService class for chaining.
   */
  addView(viewConfiguration: ViewConfiguration): ExtensibilityService;

  /**
   * Fetches a view from the view extension registry.
   *
   * @param areaId An areaId defined in the view configuration.
   * @returns A list of ViewConfigurations with a matching areaId.
   */
  getViews(areaId: string): ViewConfiguration[];
}

/**
 * Configuration for a view extension.
 */
export interface ViewConfiguration {
  /**
   * An id that corresponds to a matching extensible area.
   */
  areaId: string;
  /**
   * The unique name for this configuration.
   */
  name?: string;
  /**
   * Template string to be displayed by an extensible area.
   */
  template?: string;
  /**
   * Teplate url that specifies a html file to be displayed by an extensible area.
   */
  templateUrl?: string;
  /**
   * Component name that specifies a component to be displayed by an extensible area.
   */
  component?: string;

  /**
   * A list of paths to files that need to be lazy loaded before the view is displayed.
   */
  bundle?: string[];
}

/**
 * A map of areaId to a list of ViewConfigurations.
 */
export interface ViewExtensions {
  /**
   * A key value pair of areaId to a list of all ViewConfigurations with a matching areaId.
   */
  [areaId: string]: ViewConfiguration[];
}

export class ExtensibilityServiceImplementation implements ExtensibilityService {
  private viewExtensions: ViewExtensions = {};
  // @ngInject
  constructor(private loggingService: LoggingService) {}

  public addView(viewConfiguration: ViewConfiguration): ExtensibilityService {
    if (!this.validateConfiguration(viewConfiguration)) {
      this.loggingService.logError({
        message: "An invalid view configuration was registered.",
        configuration: viewConfiguration
      });
      return this;
    }

    if (!this.viewExtensions[viewConfiguration.areaId]) {
      this.viewExtensions[viewConfiguration.areaId] = [];
    }
    this.viewExtensions[viewConfiguration.areaId].push(viewConfiguration);
    return this;
  }

  public getViews(areaId: string) {
    return this.viewExtensions[areaId] || [];
  }

  private validateConfiguration(viewConfiguration: ViewConfiguration): boolean {
    return (
      !!viewConfiguration.areaId &&
      (!!viewConfiguration.component || !!viewConfiguration.template || !!viewConfiguration.templateUrl) &&
      (!viewConfiguration.bundle || Array.isArray(viewConfiguration.bundle))
    );
  }
}
